import { type WalletClient, type TransactionRequest, encodeFunctionData } from "viem";
import { type TransactionHash, TOKEN_IDS } from "@/types";
import { gatewayV1Abi } from "@/abi";
import { getGatewayContractAddress } from "@/config";
import { estimateContractTransactionGas, writeContractTransaction } from "@/utils";
import { prepareFormTokenAmountDecimals } from "@/utils/token";

/**
 * Prepares a redeem transaction
 */
export const prepareRedeemTransaction = async (
    amount: number,
    walletClient: WalletClient,
): Promise<TransactionRequest> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    if (!walletClient.chain?.id) {
        throw new Error("Chain ID is not available");
    }

    const contractAddress = getGatewayContractAddress(walletClient.chain.id);

    // Convert between iUSD and USDC
    const amountBigInt = prepareFormTokenAmountDecimals(amount, TOKEN_IDS.IUSD);
    const magicAmountBigInt = (amountBigInt / 10000n) * 10000n;
    const minRedeemOut = magicAmountBigInt / 1_000_000_000_000n;

    return {
        to: contractAddress,
        data: encodeFunctionData({
            abi: gatewayV1Abi,
            functionName: "redeem",
            args: [walletClient.account.address, magicAmountBigInt, minRedeemOut],
        }),
    };
};

/**
 * Executes a redeem transaction
 */
export const gatewayRedeemTransaction = async (
    amount: number,
    walletClient: WalletClient,
): Promise<TransactionHash> => {
    const txRequest = await prepareRedeemTransaction(amount, walletClient);
    const hash = await writeContractTransaction(walletClient, txRequest);
    return hash as TransactionHash;
};

/**
 * Estimates gas for a redeem transaction
 */
export const estimateRedeemTransactionGas = async (amount: number, walletClient: WalletClient): Promise<bigint> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    const txRequest = await prepareRedeemTransaction(amount, walletClient);
    return estimateContractTransactionGas(walletClient, txRequest);
};
